import React, {useEffect, useState} from "react";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {formatDateTimeShort, GlobalState} from "../../global/GlobalState";
import {Link, useParams} from "react-router-dom";
import {
	Breadcrumbs,
	Button,
	Paper, Typography
} from "@mui/material";
import {
	productCategoryColumn,
	productOrderTypeColumn,
	productStatusColumn,
	textColumn,
	tooltipColumn
} from "../../components/common/grid/Columns";
import {PentaSplit} from "../../components/common/PentaSplit";
import {GraphQlDataGrid} from "../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../services/GraphQLServices";
import {GridFilterItem} from "@mui/x-data-grid/models/gridFilterItem";
import {translateFunction} from "../../localization/TranslationService";
import {HeaderSlot, StageSwitch} from "../../components/onsite/Footer";
import {PRODUCT_COMMISSIONS_REPORT_PRODUCTS, PRODUCT_COMMISSIONS_REPORT_SETUP} from "./Queries";
import {linkProductDetailsFor} from "../products/products-details/ProductsDetails";
import {reportsDefaultTargetStage} from "./ReportModuleOverview";
import {ApolloQueryResult} from "@apollo/client";
import {ProductCommission} from "../products/models/ProductCommission";
import {ProductCommissionConfig} from "../products/models/ProductCommissionConfig";
import {ZonedDateTime} from "@js-joda/core";
import {ProductCommissionShareConfig} from "../products/models/ProductCommissionShareConfig";
import {GridColDef} from "@mui/x-data-grid";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const ProductsCommissionSetupReport = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={reportsDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/reports">{translate("Reports")}</Link>
				<Typography color="text.primary">{translate("Products commission setup report")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<ProductsIncentiveSetupReportContent/>
			</Paper>}
		/>
	</>;
}, ["Product"]);

export function ProductsIncentiveSetupReportContent(props: { tenant?: string }) {
	const [commissionLookup, setCommissionLookup] = useState<Map<string, ProductCommission>>();
	const [data, setData] = useState<any[]>([]);
	const supplierId = useParams()?.supplierId;
	const supplierKrn = supplierId ? `:product-supplier:${supplierId}:` : undefined;
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Link"),
			renderCell: (params) => (
				<Link to={linkProductDetailsFor(params.row.krn)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
			disableExport: true
		},
		{
			field: "krn",
			headerName: translate("Kizi Resource Name"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "name",
			headerName: translate("Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "clientName",
			headerName: translate("Client Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "externalId",
			headerName: translate("External ID"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "dataProviderKrn",
			headerName: translate("Data Provider KRN"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "description",
			headerName: translate("Description"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "productSupplierKrn",
			headerName: translate("Product Supplier KRN"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "productType",
			headerName: translate("Product Type"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "orderType",
			headerName: translate("Order Type"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: tooltipColumn(productOrderTypeColumn()),
			valueOptions: ["AFFILIATE", "FUNNEL_MGA", "NOT_ORDERABLE"],
			valueFormatter: (params) => formatProductOrderType(params.value),
			flex: 1,
		},
		{
			field: "productCategory",
			headerName: translate("Product Category"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: productCategoryColumn(),
			valueOptions: [
				"NON_SPECIFIC",
				"CAR",
				"BUSINESS_CAR",
				"ENERGY",
				"HOME",
				"LIABILITY",
				"TRIPLE_PLAY",
				"TRAVEL",
				"HEALTH_CARE",
				"FUNERAL",
				"LOAN",
				"SIM_ONLY",
				"ANIMAL",
				"BIKE",
				"SOLAR_PANELS",
				"SAVINGS",
				"EVENT_BASED",
				"BUSINESS_LIABILITY_INSURANCE",
				"BUSINESS_VAN",
				"BUSINESS_DAMAGE",
				"BUSINESS_ADDITIONAL_COSTS",
				"BUSINESS_LEGAL",
				"BUSINESS_COMPUTER_ELECTRONICS",
				"BUSINESS_INVENTORY",
				"BUSINESS_BUILDING",
				"BUSINESS_ACCIDENT"
			],
			valueFormatter: (params) => formatProductCategory(params.value),
			flex: 1,
		},
		{
			field: "state",
			headerName: translate("State"),
			type: "singleSelect",
			minWidth: 150,
			renderCell: tooltipColumn(productStatusColumn()),
			valueOptions: ["ACTIVE", "DELETED"],
			valueFormatter: (params) => formatProductStatus(params.value),
			flex: 1,
		},
		{
			field: "Net premium percentage",
			headerName: translate("Net premium percentage"),
			minWidth: 300,
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.commissionNetPremiumFactor ?? "";
			},
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Net premium percentage per billable person",
			headerName: translate("Net premium percentage per billable person"),
			minWidth: 300,
			renderCell: tooltipColumn(textColumn()),
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.commissionAmountPerBillablePerson ?? "";
			},
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Flat commission amount per month",
			headerName: translate("Flat commission amount per month"),
			minWidth: 300,
			renderCell: tooltipColumn(textColumn()),
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.commissionAmountPerMonth ?? "";
			},
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Number of months",
			headerName: translate("Number of months"),
			minWidth: 300,
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.numberOfMonths ?? "";
			},
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Flat commission amount per billable person",
			headerName: translate("Flat commission amount per billable person"),
			minWidth: 300,
			renderCell: tooltipColumn(textColumn()),
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.commissionAmountPerBillablePerson ?? "";
			},
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Flat commission amount",
			headerName: translate("Flat commission amount"),
			minWidth: 300,
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.commissionAmountFixedFee ?? "";
			},
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Renewal commission installment amount",
			headerName: translate("Renewal commission installment amount"),
			minWidth: 300,
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.renewalCommissionInstallmentAmount ?? "";
			},
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Renewal commission installment number of months",
			headerName: translate("Renewal commission installment number of months"),
			minWidth: 300,
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.renewalCommissionInstallmentNumberOfMonths ?? "";
			},
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Renewal commission installment link",
			headerName: translate("Renewal commission installment link"),
			minWidth: 300,
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionConfigs[0]?.renewalCommissionInstallmentAmountLink ?? "";
			},
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Partner commission percentage",
			headerName: translate("Partner commission percentage"),
			minWidth: 300,
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionShareConfigs[0]?.partnerCommissionFactor ?? "";
			},
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "Partner flat amount",
			headerName: translate("Partner flat amount"),
			minWidth: 300,
			renderCell: tooltipColumn(textColumn()),
			valueGetter: (params) => {
				return commissionLookup?.get(params.row.krn)?.commissionShareConfigs[0]?.partnerFixedAmount ?? "";
			},
			flex: 1,
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			type: "dateTime",
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			type: "dateTime",
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		}
	];
	const stage = GlobalState.targetKiziStage(reportsDefaultTargetStage);
	
	function setTheData(items: any[]) {
		if (JSON.stringify(data) !== JSON.stringify(items))
			setData(items);
	}
	
	useEffect(() => {
		if (data.length > 0) {
			const krns = data.map(x => x.krn);
			getProductCommissions(stage, krns).then((items) => {
				const nodes: ProductCommission[] = items;
				removeOtherCommissionConfig(nodes);
				setCommissionLookup(nodes.associateBy(it => it.krn, it => it));
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(data)]);
	
	return <GraphQlDataGrid
		key={`products-grid-${props.tenant ?? "none"}`}
		rows={[]}
		query={PRODUCT_COMMISSIONS_REPORT_PRODUCTS}
		graphFunction={"products"}
		client={graphQLServices.productsClient}
		rowsPerPageOptions={[50, 500, 1000, 2000, 5000]}
		initialPageSize={50}
		columns={columns}
		getRowId={row => row.krn}
		onDataFetched={setTheData}
		onDataRefresh={() => setData([])}
		defaultSortModel={[
			{
				field: "name",
				sort: "asc"
			}
		]}
		// default filter as described in SUR-136
		defaultFilterModel={{
			items: [
				{
					field: "state",
					value: "ACTIVE",
					operator: "is",
				},
				{
					field: "orderType",
					value: ["AFFILIATE", "FUNNEL_MGA"],
					operator: "isAnyOf",
				},
				(supplierKrn && {
					field: "krn",
					value: supplierKrn,
					operator: "contains",
				}) as GridFilterItem,
				(props.tenant && {
					field: "krn",
					value: `krn:${props.tenant}:`,
					operator: "contains",
				}) as GridFilterItem,
			].filter(it => !!it)
		}}
		queryVariables={{stage}}
		initialState={{
			columns: {
				columnVisibilityModel: {
					dataProviderKrn: false,
					description: false,
					productSupplierKrn: false,
					productType: false,
					productTypeKrn: false,
					affiliateUrl: false,
					insuranceCardUrl: false,
					termsAndConditionsUrl: false,
					supplierGenericProduct: false,
					creationDateTime: false,
				}
			}
		}}
	/>;
}

async function getProductCommissions(stage: string, krns: string[]): Promise<ProductCommission[]> {
	const response: ApolloQueryResult<any> = await graphQLServices.productsClient.query(
		{
			query: PRODUCT_COMMISSIONS_REPORT_SETUP,
			variables: {
				krns,
				pageSize: krns.length,
				stage
			},
			fetchPolicy: "no-cache"
		}
	);
	if (response.data?.productCommissions?.nodes === null)
		return [];
	
	
	return response.data.productCommissions.nodes;
}

function removeOtherCommissionConfig(items: ProductCommission[]): void {
	
	items.forEach(item => {
		const commissionConfigs = item.commissionConfigs;
		const commissionShareConfigs = item.commissionShareConfigs;
		if (commissionConfigs && commissionConfigs.length > 0) {
			const now = ZonedDateTime.now();
			const sortable: SortableProductCommissionConfig[] = commissionConfigs.map(x => x as SortableProductCommissionConfig);
			// noinspection DuplicatedCode
			sortable.forEach((x) => x.zonedStartDate = ZonedDateTime.parse(x.startDate));
			const currentItem = sortable.filter((item) => {
				const startDate = ZonedDateTime.parse(item.startDate);
				return startDate && startDate.compareTo(now) < 0;
				
			}).sort((a, b) => b.zonedStartDate.compareTo(a.zonedStartDate))[0];
			
			item.commissionConfigs = [currentItem];
		}
		
		if (commissionShareConfigs && commissionShareConfigs.length > 0) {
			const now = ZonedDateTime.now();
			const sortable: SortableProductCommissionShareConfig[] = commissionShareConfigs.map(x => x as SortableProductCommissionShareConfig);
			// noinspection DuplicatedCode
			sortable.forEach((x) => x.zonedStartDate = ZonedDateTime.parse(x.startDate));
			const currentItem = sortable.filter((item) => {
				const startDate = ZonedDateTime.parse(item.startDate);
				return startDate.compareTo(now) < 0;
				
			}).sort((a, b) => b.zonedStartDate.compareTo(a.zonedStartDate))[0];
			
			item.commissionShareConfigs = [currentItem];
		}
	});
}

interface SortableProductCommissionConfig extends ProductCommissionConfig {
	zonedStartDate: ZonedDateTime;
}

interface SortableProductCommissionShareConfig extends ProductCommissionShareConfig {
	zonedStartDate: ZonedDateTime;
}

function formatProductStatus(text: string | null): string {
	return formatEnum("productStatus", text);
}

function formatProductOrderType(text: string | null): string {
	return formatEnum("productOrderType", text);
}

function formatProductCategory(text: string | null): string {
	return formatEnum("productCategory", text);
}

function formatEnum(prefix: string, text: string | null): string {
	if (!text)
		return "";
	return translate(text)
		?? translate("Unknown");
}
